/**
 * ActivityAuditDrilldown — sub-route for KPI drilldown.
 *
 * Route: /#/activity/drilldown?kpi=conversations&window=today&role=bdr
 *
 * LOCKED DECISIONS:
 *   A4  Sub-route pattern (bookmarkable, hash-routing aligned)
 *
 * TENANT ISOLATION: tenant_id != 2 enforced.
 * DATA STATUS: Mock/seeded. Real data wired in Option B (May 12).
 *
 * Drilldown shows the underlying activity records filtered by:
 *   - kpi_key  (e.g., conversations, calls_made, meetings_booked, etc.)
 *   - window   (today / week / month / quarter / year)
 *   - role     (bdr / ae / manager / executive)
 *   - rep      (optional — Manager clicking into a rep)
 */

import { useLocation } from "wouter";
import { tokens } from "@/components/operator/tokens";
import OperatorShell from "@/components/operator/OperatorShell";
import { parseDrillParams } from "@/lib/drillUrl";
import {
  BDR_ACTIVITIES,
  AE_ACTIVITIES,
  type BdrActivityRow,
  type AeActivityRow,
  type TimeWindow,
} from "./audit-mock-data";

function parseParams(search: string): Record<string, string> {
  const raw = search.startsWith("?") ? search.slice(1) : search;
  const out: Record<string, string> = {};
  raw.split("&").forEach((part) => {
    const [k, v] = part.split("=");
    if (k) out[decodeURIComponent(k)] = decodeURIComponent(v ?? "");
  });
  return out;
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function OutcomePill({ outcome }: { outcome: string }) {
  const colors: Record<string, { fg: string; bg: string }> = {
    "Connected":         { fg: "#166534", bg: "#DCFCE7" },
    "Voicemail":         { fg: "#92400E", bg: "#FEF3C7" },
    "No Answer":         { fg: "#6B6B70", bg: "#EFEFEE" },
    "Replied":           { fg: "#0B0B0F", bg: "#EFEFEE" },
    "Booked":            { fg: "#166534", bg: "#DCFCE7" },
    "Not Interested":    { fg: "#DC2626", bg: "#FEE2E2" },
    "Qualified-Forward": { fg: "#DC2626", bg: "#FEE2E2" },
  };
  const s = colors[outcome] ?? { fg: "#6B6B70", bg: "#EFEFEE" };
  return (
    <span style={{ background: s.bg, color: s.fg, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "2px 8px", borderRadius: 999 }}>
      {outcome}
    </span>
  );
}

// Map kpi_key → relevant activity_types for filtering
const KPI_TYPE_MAP: Record<string, string[]> = {
  conversations:      ["call", "email", "linkedin", "sms", "in_person"],
  calls_made:         ["call"],
  emails_sent:        ["email"],
  meetings_booked:    ["meeting"],
  qualified_forward:  ["call"], // filter by outcome: "Qualified-Forward"
  meetings_held:      ["meeting"],
  demos_delivered:    ["demo"],
  proposals_out:      ["proposal"],
  deals_advanced:     ["meeting", "demo", "proposal"],
  deals_closed:       ["meeting"],
};

export default function ActivityAuditDrilldown() {
  const [location, setLocation] = useLocation();

  // Parse hash query params — wouter provides location as path only,
  // so we read window.location.hash directly.
  const hashSearch = typeof window !== "undefined"
    ? window.location.hash.split("?")[1] ?? ""
    : "";
  const params = parseParams(hashSearch);

  const kpiKey    = params.kpi    ?? "conversations";
  const window_   = (params.window ?? "today") as TimeWindow;
  const role      = params.role   ?? "bdr";
  const repId     = params.rep    ?? null;

  const kpiLabel = kpiKey.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  // Filter BDR activities to match kpi
  const types = KPI_TYPE_MAP[kpiKey] ?? [];

  let bdrRows: BdrActivityRow[] = BDR_ACTIVITIES.filter((a) => {
    if (a.tenant_id === 2) return false; // NEVER show tenant 2
    if (types.length && !types.includes(a.activity_type)) return false;
    if (kpiKey === "qualified_forward" && a.outcome !== "Qualified-Forward") return false;
    if (repId && a.user_id !== repId) return false;
    return true;
  });

  const aeRows: AeActivityRow[] = AE_ACTIVITIES.filter((a) => {
    if (a.tenant_id === 2) return false;
    if (repId && a.user_id !== repId) return false;
    return true;
  });

  const isBdr = role === "bdr" || (role === "manager" && kpiKey.startsWith("team_conversations"));
  const rows = isBdr ? bdrRows : [];

  return (
    <OperatorShell
      breadcrumbs={[
        { label: "Home", href: "/" },
        { label: "Activity Audit", href: "/dashboards/bdr?tab=activity" },
        { label: `Drilldown: ${kpiLabel}` },
      ]}
      pageTitle={`Drilldown: ${kpiLabel}`}
      pageMeta={`${window_.charAt(0).toUpperCase() + window_.slice(1)} · ${role.toUpperCase()} view · ${rows.length} records`}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Breadcrumb back */}
        <button
          type="button"
          onClick={() => setLocation(-1 as unknown as string)}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "none", border: "none",
            fontSize: 13, color: tokens.color.textMuted, cursor: "pointer",
            padding: 0, fontFamily: tokens.font.body,
          }}
        >
          ← Back to Activity Audit
        </button>

        {/* Context chips */}
        <div style={{ display: "flex", gap: 8 }}>
          {[
            { label: "KPI", value: kpiLabel },
            { label: "Window", value: window_.charAt(0).toUpperCase() + window_.slice(1) },
            { label: "Role", value: role.toUpperCase() },
            ...(repId ? [{ label: "Rep", value: repId }] : []),
          ].map(({ label, value }) => (
            <div
              key={label}
              style={{
                background: tokens.color.surfaceMute,
                border: `1px solid ${tokens.color.border}`,
                borderRadius: 6, padding: "4px 10px",
                fontSize: 12,
              }}
            >
              <span style={{ color: tokens.color.textSubtle, fontWeight: 500 }}>{label}: </span>
              <span style={{ color: tokens.color.textPrimary, fontWeight: 600 }}>{value}</span>
            </div>
          ))}
        </div>

        {/* Seed data notice */}
        <div style={{ background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 6, padding: "7px 14px", fontSize: 12, color: "#92400E", fontWeight: 500 }}>
          Sample data — live Supabase wiring in progress.
          Tenant 2 (Mi6 Demo) excluded from all queries.
        </div>

        {/* Table */}
        <div style={{ background: "#FFFFFF", border: `1px solid ${tokens.color.border}`, borderRadius: tokens.radius.card, overflow: "hidden" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: role === "bdr" || role === "manager"
                ? "130px 90px 1fr 1fr 130px 1fr"
                : "130px 100px 1fr 80px 1fr 1fr",
              gap: 12, padding: "9px 16px",
              borderBottom: `1px solid ${tokens.color.border}`,
              background: tokens.color.surfaceMute,
            }}
          >
            {(role === "bdr" || role === "manager"
              ? ["Time", "Channel", "Contact", "Company", "Outcome", "Notes"]
              : ["Time", "Type", "Account", "Stage", "Outcome", "Next Step"]
            ).map((h) => (
              <div key={h} style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: tokens.color.textSubtle }}>{h}</div>
            ))}
          </div>

          {rows.length === 0 ? (
            <div style={{ padding: "40px 16px", textAlign: "center", fontSize: 13, color: tokens.color.textSubtle }}>
              No records match this drilldown for {window_}.
            </div>
          ) : (
            rows.map((row) => {
              const bRow = row as BdrActivityRow;
              return (
                <div
                  key={bRow.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "130px 90px 1fr 1fr 130px 1fr",
                    gap: 12, padding: "11px 16px",
                    borderBottom: `1px solid ${tokens.color.borderSoft}`,
                    alignItems: "start",
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = tokens.color.surfaceMute; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
                >
                  <div style={{ fontSize: 11, color: tokens.color.textSubtle, fontVariantNumeric: "tabular-nums" }}>
                    {fmtTime(bRow.logged_at)}
                  </div>
                  <div style={{ fontSize: 12, color: tokens.color.textMuted }}>{bRow.channel}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: tokens.color.textPrimary }}>{bRow.contact}</div>
                  <div style={{ fontSize: 12, color: tokens.color.textMuted }}>{bRow.company}</div>
                  <OutcomePill outcome={bRow.outcome} />
                  <div style={{ fontSize: 11, color: tokens.color.textSubtle, lineHeight: 1.5 }}>
                    {bRow.notes.length > 80 ? bRow.notes.slice(0, 80) + "…" : bRow.notes}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div style={{ fontSize: 11, color: tokens.color.textSubtle }}>
          URL: /#/activity/drilldown?kpi={kpiKey}&window={window_}&role={role}
          {repId ? `&rep=${repId}` : ""}
          {" "}— bookmarkable, hash-routing aligned (decision A4).
        </div>
      </div>
    </OperatorShell>
  );
}
